import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { MapPin, Trash2, Download, Shield, AlertCircle, Zap } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { usePerformance } from "@/contexts/PerformanceContext";

export const PrivacySettings = () => {
  const [locationConsent, setLocationConsent] = useState<boolean | null>(null);
  const [analyticsConsent, setAnalyticsConsent] = useState(true);
  const { lightModeEnabled, toggleLightMode, isSmartTV, isLowPowerDevice } = usePerformance();

  useEffect(() => {
    // Load preferences from localStorage
    const consent = localStorage.getItem('location_consent');
    setLocationConsent(consent === 'granted' ? true : consent === 'denied' ? false : null);
    
    const analytics = localStorage.getItem('analytics_consent');
    setAnalyticsConsent(analytics !== 'denied');
  }, []);

  const handleLocationConsentChange = (enabled: boolean) => {
    localStorage.setItem('location_consent', enabled ? 'granted' : 'denied');
    setLocationConsent(enabled);
    
    if (!enabled) {
      // Clear cached location data
      localStorage.removeItem('location_cache_v1');
    }
    
    toast.success(
      enabled 
        ? 'Location tracking enabled. Refresh the page for changes to take effect.' 
        : 'Location tracking disabled. Your cached location data has been cleared.'
    );
  };

  const handleAnalyticsConsentChange = (enabled: boolean) => {
    localStorage.setItem('analytics_consent', enabled ? 'granted' : 'denied');
    setAnalyticsConsent(enabled);
    toast.success(enabled ? 'Analytics enabled' : 'Analytics disabled');
  };

  const handleDeleteLocationData = () => {
    // Clear local cache
    localStorage.removeItem('location_cache_v1');
    
    // Note: Server-side deletion would require API call to backend
    toast.success(
      'Local location cache cleared. For complete data deletion including server records, please contact us.',
      { duration: 5000 }
    );
  };

  const handleExportData = () => {
    const cache = localStorage.getItem('location_cache_v1');
    const consent = localStorage.getItem('location_consent');
    const analytics = localStorage.getItem('analytics_consent');
    
    const exportData = {
      locationCache: cache ? JSON.parse(cache) : null,
      locationConsent: consent,
      analyticsConsent: analytics,
      exportDate: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `privacy-data-export-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Your data has been exported');
  };

  return (
    <div className="space-y-6">
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertDescription>
          These settings control how we collect and use your data. Changes take effect immediately.
          For server-side data deletion, please contact us via our{' '}
          <a href="/contact" className="text-primary hover:underline">contact page</a>.
        </AlertDescription>
      </Alert>

      {/* Location Tracking */}
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <MapPin className="w-6 h-6 text-primary mt-1" />
          <div className="flex-1 space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">Location Tracking</h3>
              <p className="text-sm text-muted-foreground">
                Allow us to collect your GPS coordinates, street address, city, and country for 
                analytics and personalized experiences. Data is retained for 90 days.
              </p>
            </div>
            
            <div className="flex items-center justify-between py-2">
              <Label htmlFor="location-tracking" className="cursor-pointer">
                {locationConsent === null ? 'Not set' : locationConsent ? 'Enabled' : 'Disabled'}
              </Label>
              <Switch
                id="location-tracking"
                checked={locationConsent === true}
                onCheckedChange={handleLocationConsentChange}
              />
            </div>

            {locationConsent === null && (
              <Alert variant="default" className="bg-amber-500/10 border-amber-500/20">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <AlertDescription className="text-amber-900 dark:text-amber-100">
                  You haven't set your location tracking preference yet. 
                  You'll be prompted when we need location data.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      </Card>

      {/* Analytics */}
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <Shield className="w-6 h-6 text-primary mt-1" />
          <div className="flex-1 space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">Analytics & Usage Data</h3>
              <p className="text-sm text-muted-foreground">
                Allow us to collect anonymous usage statistics to improve our service. 
                This includes page views, feature usage, and error reports.
              </p>
            </div>
            
            <div className="flex items-center justify-between py-2">
              <Label htmlFor="analytics" className="cursor-pointer">
                {analyticsConsent ? 'Enabled' : 'Disabled'}
              </Label>
              <Switch
                id="analytics"
                checked={analyticsConsent}
                onCheckedChange={handleAnalyticsConsentChange}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Performance / Light Mode */}
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <Zap className="w-6 h-6 text-primary mt-1" />
          <div className="flex-1 space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">Performance Mode</h3>
              <p className="text-sm text-muted-foreground">
                Enable light mode to disable animations, confetti, audio, and heavy features. 
                Recommended for Smart TVs and low-power devices for optimal performance.
              </p>
              {(isSmartTV || isLowPowerDevice) && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 font-medium">
                  {isSmartTV ? '📺 Smart TV detected' : '⚡ Low-power device detected'} - Light mode recommended
                </p>
              )}
            </div>
            
            <div className="flex items-center justify-between py-2">
              <Label htmlFor="light-mode" className="cursor-pointer">
                {lightModeEnabled ? 'Light Mode (Enabled)' : 'Full Mode (All Features)'}
              </Label>
              <Switch
                id="light-mode"
                checked={lightModeEnabled}
                onCheckedChange={toggleLightMode}
              />
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p><strong>Light mode disables:</strong></p>
              <ul className="ml-4 space-y-0.5">
                <li>• Slot machine animations</li>
                <li>• Confetti effects</li>
                <li>• Voice announcements</li>
                <li>• Background audio</li>
                <li>• Heavy visual effects</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>

      {/* Data Management */}
      <Card className="p-6">
        <h3 className="font-semibold text-lg mb-4">Data Management (GDPR/CCPA Rights)</h3>
        <div className="space-y-3">
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={handleExportData}
          >
            <Download className="w-4 h-4 mr-2" />
            Export My Data (JSON)
          </Button>
          
          <Button
            variant="outline"
            className="w-full justify-start text-destructive hover:text-destructive"
            onClick={handleDeleteLocationData}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Cached Location Data
          </Button>

          <div className="bg-muted/50 rounded-lg p-4 mt-4">
            <p className="text-sm text-muted-foreground">
              <strong>Complete Data Deletion:</strong> To request complete deletion of all your data 
              from our servers (including session history, tracking data, and account information), 
              please{' '}
              <a href="/contact" className="text-primary hover:underline">contact us</a>
              {' '}with your email address. We'll process your request within 30 days as required by GDPR.
            </p>
          </div>
        </div>
      </Card>

      {/* Information */}
      <Card className="p-6 bg-muted/50">
        <h3 className="font-semibold text-lg mb-3">Privacy Information</h3>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• Location data is automatically deleted after 90 days</li>
          <li>• IP addresses are masked (last octet removed) after 7 days</li>
          <li>• You can request a full data export at any time</li>
          <li>• Disabling tracking doesn't affect core app functionality</li>
          <li>• Changes are effective immediately</li>
          <li>• Read our full{' '}
            <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>
          </li>
        </ul>
      </Card>
    </div>
  );
};
